import api from "@/api/api";
// @ts-ignore
import { computed, ref, watch } from "@vue/runtime-core";
import { defineStore } from "pinia";
// @ts-ignore
import type { Support, SupportType } from "@/types/support";

import cacheService from "@/services/cache.service";
import { mapSocialMediaFormatToBackend } from "@/utils/supportUtils";
import axios, { AxiosError } from "axios";
import { useToast } from "vue-toastification";
import { useProductCustomizationStore } from "./useProductCustomizationStore";
import { usePdfJobsStore } from "./usePdfJobsStore";

interface SupportHeader {
  id: number;
  type: string;
  [key: string]: any;
}

export const useSupportStore = defineStore("support", () => {
  const toast = useToast();
  const productCustomizationStore = useProductCustomizationStore();

  // État
  const currentSupport = ref<Support | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const isEditing = ref(false);
  const isPreview = ref(false);
  const supportType = ref<SupportType>("affiche");

  // Configuration pour la génération PDF automatique.
  // Désactivé : le PDF maître (vectoriel) est désormais généré par
  // persistSupportPdf -> /generate-vector-pdf. L'ancien auto-trigger HQ
  // (GenerateHighQualityPdfJob) faisait doublon et plantait sur certains
  // supports. Le bouton "HQ" manuel reste fonctionnel.
  const autoGenerateHighQualityPdf = ref(false);

  // Sons pour les notifications
  const successSound = new Audio("/sounds/success.wav");
  const errorSound = new Audio("/sounds/error.mp3");

  // Ajuster le volume des sons
  successSound.volume = 0.3; // 30% du volume pour le son iPhone
  errorSound.volume = 0.15; // 15% du volume pour l'erreur

  // Getters
  const supportId = computed(() => currentSupport.value?.id);
  const isNewSupport = computed(() => !supportId.value);

  // Service de gestion des en-têtes de support
  const supportHeadersService = {
    headers: ref<Map<string, SupportHeader[]>>(new Map()),
    isLoading: ref(false),
    error: ref<string | null>(null),

    async fetchHeaders(
      brandSlug: string,
      type?: string
    ): Promise<SupportHeader[]> {
      if (this.isLoading.value) {
        // Attendre que le chargement en cours soit terminé
        return new Promise((resolve) => {
          const unwatch = watch(this.isLoading, (newValue) => {
            if (!newValue) {
              unwatch();
              resolve(this.getHeaders(brandSlug, type));
            }
          });
        });
      }

      const cacheKey = type ? `${brandSlug}-${type}` : brandSlug;

      // Vérifier d'abord le cache persistant
      const persistentCacheKey = `support_headers_${cacheKey}`;
      const cachedHeaders =
        cacheService.get<SupportHeader[]>(persistentCacheKey);
      if (cachedHeaders) {
        this.headers.value.set(cacheKey, cachedHeaders);
        return this.getHeaders(brandSlug, type);
      }

      if (this.headers.value.has(cacheKey)) {
        return this.getHeaders(brandSlug, type);
      }

      try {
        this.isLoading.value = true;
        this.error.value = null;

        const response = await api.get("/support_headers", {
          params: {
            "groups.slug": brandSlug,
            isActive: 1,
            ...(type && { type }),
          },
        });

        const headers = (response.data.data ||
          response.data) as SupportHeader[];
        if (headers && headers.length > 0) {
          // Mettre en cache pour la marque spécifique
          this.headers.value.set(cacheKey, headers);
          // Mettre en cache persistant pour 2 heures
          cacheService.set(persistentCacheKey, headers, 2);
          // Mettre en cache pour la marque générale
          if (type) {
            this.headers.value.set(brandSlug, headers);
          }
        }

        return this.getHeaders(brandSlug, type);
      } catch (error) {
        this.error.value =
          error instanceof Error ? error.message : "Une erreur est survenue";
        console.error("Erreur lors de la récupération des en-têtes:", error);
        return [];
      } finally {
        this.isLoading.value = false;
      }
    },

    getHeaders(brandSlug: string, type?: string): SupportHeader[] {
      const cacheKey = type ? `${brandSlug}-${type}` : brandSlug;
      return this.headers.value.get(cacheKey) || [];
    },

    clearCache() {
      this.headers.value.clear();
    },
  };

  // Actions
  const loadSupport = async (
    type: SupportType,
    id: string,
    isPublic = false
  ) => {
    try {
      isLoading.value = true;
      error.value = null;

      const url = isPublic ? `/supports/public/${id}` : `/supports/${id}`;
      const headers: Record<string, string> = {};
      const publicToken =
        localStorage.getItem("auth_token") ||
        import.meta.env.VITE_PUBLIC_API_TOKEN;
      if (isPublic && publicToken) {
        headers["Authorization"] = `Bearer ${publicToken}`;
      }
      const response = await api.get(url, { headers });

      const supportData = response.data.data;
      if (!supportData) {
        throw new Error("Données du support invalides");
      }

      // Enrichir les images produits avant de stocker le support
      const supportWithImages = await enrichirImagesProduits(supportData);
      if (!supportWithImages) {
        throw new Error("Erreur lors de l'enrichissement des images");
      }

      // Pour les supports sociaux, extraire les produits depuis les éléments des pages
      if (type === "social" && Array.isArray(supportWithImages.pages)) {
        const products: any[] = [];
        for (const page of supportWithImages.pages) {
          if (Array.isArray(page.elements)) {
            for (const element of page.elements) {
              if (
                element &&
                (element.type === "product" ||
                  element.product_id ||
                  element.product_image_id)
              ) {
                // Créer un objet produit à partir de l'élément
                const product = {
                  id:
                    element.product_id ||
                    (element.product_image as any)?.product_id,
                  name: element.name,
                  description: element.description,
                  price: element.price_ht,
                  product_image_id: element.product_image_id,
                  brand_id: element.brand_id,
                  image: (element as any).image,
                  hook: element.hook,
                };

                products.push(product);
              }
            }
          }
        }
        // Si aucun produit n'a été trouvé, initialiser avec un slot vide
        if (products.length === 0) {
          products.push(null);
        }
        supportWithImages.products = products;
      }

      // Charger les paramètres de personnalisation des produits
      if (Array.isArray(supportWithImages.pages)) {
        for (const page of supportWithImages.pages) {
          if (Array.isArray(page.elements)) {
            for (const element of page.elements) {
              if (
                element &&
                element.type === "product" &&
                element.product_id &&
                element.element_settings
              ) {
                // Charger les paramètres de personnalisation dans le store
                productCustomizationStore.setCustomization(element.product_id, {
                  removeBackground:
                    element.element_settings.removeBackground || false,
                  backgroundColor:
                    element.element_settings.backgroundColor || "#fff",
                  showPrice: element.element_settings.showPrice ?? true,
                  priceScale: element.element_settings.priceScale || 1,
                  priceSize: element.element_settings.priceSize || "price-md",
                  priceTTCSize:
                    element.element_settings.priceTTCSize || "price-ttc-md",
                  pictogram: element.element_settings.pictogram || null,
                  pictoScale: element.element_settings.pictoScale || 1,
                  brand: element.element_settings.brand || null,
                  brandScale: element.element_settings.brandScale || 1,
                  header: element.element_settings.header || null,
                  titleScale: element.element_settings.titleScale || 1,
                  position: element.element_settings.position || {
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0,
                  },
                  rotation: element.element_settings.rotation || 0,
                  z_index: element.element_settings.z_index || 0,
                });
              }
            }
          }
        }
      }

      // Synchroniser nb_pages avec le nombre réel de pages
      supportWithImages.nb_pages = supportWithImages.pages.length;

      // S'assurer que le nombre de pages est correctement défini
      if (
        type === "flyer" &&
        (!supportWithImages.nb_pages || supportWithImages.nb_pages < 2)
      ) {
        supportWithImages.nb_pages = 2;
      }

      currentSupport.value = supportWithImages;
      supportType.value = type;

      // Mettre à jour le store avec les informations de pages
      updateSupport({
        nb_pages: supportWithImages.nb_pages,
        pages: supportWithImages.pages,
        network: supportWithImages.network,
        format: supportWithImages.format,
      });
    } catch (e) {
      error.value = e instanceof Error ? e.message : "Une erreur est survenue";
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const initSupport = async (type: SupportType) => {
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const brandSlug = localStorage.getItem("brand") || "hv";

    // Récupérer les en-têtes de support
    let supportHeaderId = 1;
    try {
      const headers = await supportHeadersService.fetchHeaders(brandSlug, type);

      if (headers && headers.length > 0) {
        // Chercher d'abord un en-tête spécifique au type
        const typeSpecificHeader = headers.find((h: any) => h.type === type);
        if (typeSpecificHeader) {
          supportHeaderId = typeSpecificHeader.id;
        } else {
          // Sinon, utiliser le premier en-tête disponible
          supportHeaderId = headers[0].id;
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des en-têtes:", error);
      supportHeaderId = 1;
    }
    let supportTemplateId = 1;

    if (type === "flyer") {
      supportTemplateId = 2;
    } else if (type === "depliant") {
      supportTemplateId = 3;
    } else if (type === "banderole") {
      supportTemplateId = 5;
    } else if (type === "emailing") {
      supportTemplateId = 4;
    } else if (type === "affiche") {
      supportTemplateId = 1;
    } else if (type === "social") {
      supportTemplateId = 6;
    }

    // S'assurer que l'owner_id est défini
    const ownerId = userData.id || 1;

    // Déterminer automatiquement le champ agco en fonction des rôles de l'utilisateur
    let isAgcoOnly = false;
    if (userData.roles && Array.isArray(userData.roles)) {
      // L'utilisateur a le rôle AGCO (peu importe les autres rôles)
      isAgcoOnly = userData.roles.includes("ROLE_AGCO");
    }

    // Définir le support_group_id en fonction de la marque
    let supportGroupId = 1; // HV par défaut
    const brand = localStorage.getItem("brand") || "hv";
    if (brand === "valtra") {
      supportGroupId = 2;
    } else if (brand === "fendt") {
      supportGroupId = 3;
    }

    supportType.value = type;
    currentSupport.value = {
      type,
      name: "Votre titre",
      dates: new Date().toISOString().split("T")[0],
      price_type: 1,
      support_template_id: supportTemplateId,
      published: false,
      agco: isAgcoOnly,
      support_header_id: supportHeaderId,
      owner_id: ownerId,
      nb_pages: type === "flyer" ? 2 : type === "depliant" ? 4 : 1,
      support_group_id: supportGroupId,
      highlight_bg: false,
      format: "A4",
      highlight_bg_color: "#FFFFFF",
      display_offre: "none",
      date_publish: null,
      zoom_level: "1.00",
      background_color: "#FFFFFF",
      font_family: "Arial",
      font_size: 12,
      layout_settings: {
        gridEnabled: true,
        gridSize: 10,
        snapToGrid: true,
        showGuides: true,
        showRulers: true,
      },
      custom_styles: [],
      pages: Array.from(
        { length: type === "depliant" ? 4 : type === "flyer" ? 2 : 1 },
        (_, i) => ({
          id: i,
          page_number: i + 1,
          support_id: 0,
          nb_max_products: 5,
          page_settings: {
            margin_top: 10,
            margin_bottom: 10,
            margin_left: 10,
            margin_right: 10,
            bleed: 5,
            safe_area: true,
          },
          background_image: null,
          page_zoom: "1.00",
          page_rotation: 0,
          page_position: {
            x: 0,
            y: 0,
            width: 595,
            height: 842,
          },
          elements: [],
        })
      ),
      products: type === "social" || type === "emailing" ? [null] : [],
    };
  };

  const updateSupport = (support: Partial<Support>) => {
    if (currentSupport.value) {
      currentSupport.value = { ...currentSupport.value, ...support };
    }
  };

  // Fonction utilitaire pour enrichir les éléments produits avec leur URL d'image
  async function enrichirImagesProduits(support: any): Promise<Support> {
    if (!support || !Array.isArray(support.pages)) {
      console.error("Support invalide ou pages non définies:", support);
      return support as Support;
    }

    try {
      const allImages = (await api.get("/product_images")).data;

      for (const page of support.pages) {
        if (!Array.isArray(page.elements)) {
          console.error("Éléments de page invalides:", page);
          continue;
        }

        for (const element of page.elements) {
          if (element.product_image_id) {
            const image = allImages.find(
              (img: any) => img.id === element.product_image_id
            );
            if (image) {
              const imagePath = image.image_path && image.image_path.startsWith('products/')
                ? image.image_path
                : `products/${image.image_path || image.image_name}`;
              (element as any).image = `${
                import.meta.env.VITE_API_URL
              }/upl/${imagePath}`;
              (element as any).product_image_real_id = image.id;
            } else {
              (element as any).image = null;
            }
          }
        }
      }
      return support as Support;
    } catch (error) {
      console.error("Erreur lors de l'enrichissement des images:", error);
      return support as Support;
    }
  }

  function prepareSupportForSave(support: any) {
    if (!support || !Array.isArray(support.pages)) {
      console.error("Support invalide ou pages non définies:", support);
      return support;
    }

    const preparedSupport = { ...support };

    // Si le support a déjà un support_header_id valide, le conserver
    if (preparedSupport.support_header_id && Number(preparedSupport.support_header_id) > 0) {
      preparedSupport.support_header_id = Number(preparedSupport.support_header_id);
    } else {
      // Sinon, récupérer un support_header_id valide
      const brandSlug = localStorage.getItem("brand") || "hv";

      const headers = supportHeadersService.getHeaders(
        brandSlug,
        supportType.value
      );

      if (headers && headers.length > 0) {
        // Chercher d'abord un en-tête spécifique au type
        const typeSpecificHeader = headers.find(
          (h: SupportHeader) => h.type === supportType.value
        );
        if (typeSpecificHeader) {
          preparedSupport.support_header_id = typeSpecificHeader.id;
        } else {
          // Sinon, utiliser le premier en-tête disponible
          preparedSupport.support_header_id = headers[0].id;
        }
      } else {
        // Si aucun en-tête n'est trouvé, essayer de les récupérer
        supportHeadersService
          .fetchHeaders(brandSlug, supportType.value)
          .then((newHeaders) => {
            if (newHeaders && newHeaders.length > 0) {
              const typeSpecificHeader = newHeaders.find(
                (h: SupportHeader) => h.type === supportType.value
              );
              preparedSupport.support_header_id = typeSpecificHeader
                ? typeSpecificHeader.id
                : newHeaders[0].id;
            } else {
              preparedSupport.support_header_id = 1;
            }
          })
          .catch((error) => {
            console.error("Erreur lors de la récupération des en-têtes:", error);
            preparedSupport.support_header_id = 1;
          });
      }

      // S'assurer que support_header_id est un nombre valide
      preparedSupport.support_header_id = Number(
        preparedSupport.support_header_id
      );
      if (isNaN(preparedSupport.support_header_id)) {
        console.warn(
          "support_header_id invalide, utilisation de la valeur par défaut"
        );
        preparedSupport.support_header_id = 1;
      }
    }

    preparedSupport.owner_id = Number(support.owner_id) || 1;

    // Déterminer automatiquement le champ agco en fonction des rôles de l'utilisateur
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    if (userData.roles && Array.isArray(userData.roles)) {
      // Si l'utilisateur a le rôle AGCO, tous ses documents sont dans Documents AGCO
      preparedSupport.agco = userData.roles.includes("ROLE_AGCO");
    } else {
      preparedSupport.agco = false;
    }

    // Recalculer le support_group_id en fonction de la marque actuelle
    const brand = localStorage.getItem("brand") || "hv";
    let supportGroupId = 1; // HV par défaut
    if (brand === "valtra") {
      supportGroupId = 2;
    } else if (brand === "fendt") {
      supportGroupId = 3;
    }
    preparedSupport.support_group_id = supportGroupId;

    // Mapper le format pour les supports sociaux
    if (supportType.value === "social" && support.format) {
      preparedSupport.format = mapSocialMediaFormatToBackend();
    } else {
      preparedSupport.format = support.format || "A4";
    }
    preparedSupport.network = support.network || "none";
    preparedSupport.name = support.name || preparedSupport.name;
    preparedSupport.dates =
      support.dates ||
      preparedSupport.validity_period ||
      new Date().toISOString().split("T")[0];
    preparedSupport.price_type = Number(support.price_type) || 1;
    preparedSupport.published = Boolean(support.published);
    preparedSupport.highlight_bg = Boolean(support.highlight_bg);
    preparedSupport.highlight_bg_color =
      support.highlight_bg_color || "#FFFFFF";
    preparedSupport.display_offre = support.display_offre || "none";
    preparedSupport.date_publish = support.date_publish || null;
    preparedSupport.zoom_level = support.zoom_level || "1.00";
    preparedSupport.background_color = support.background_color || "#FFFFFF";
    preparedSupport.font_family = support.font_family || "Arial";
    preparedSupport.font_size = Number(support.font_size) || 12;
    preparedSupport.layout_settings = support.layout_settings || {
      gridEnabled: true,
      gridSize: 10,
      snapToGrid: true,
      showGuides: true,
      showRulers: true,
    };
    preparedSupport.custom_styles = support.custom_styles || [];
    preparedSupport.products = support.products || [];
    preparedSupport.html = support.html || "";

    preparedSupport.pages = support.pages.map((page: any) => {
      const preparedPage = { ...page };
      preparedPage.background_image = page.background_image || null;
      preparedPage.network = support.network || "none";
      // Mapper le format pour les supports sociaux
      if (supportType.value === "social" && support.format) {
        preparedPage.format = mapSocialMediaFormatToBackend();
      } else {
        preparedPage.format = support.format || "A4";
      }
      preparedPage.page_settings = page.page_settings || {
        margin_top: 10,
        margin_bottom: 10,
        margin_left: 10,
        margin_right: 10,
        bleed: 5,
        safe_area: true,
      };
      preparedPage.page_zoom = page.page_zoom || "1.00";
      preparedPage.page_rotation = Number(page.page_rotation) || 0;
      preparedPage.page_position = page.page_position || {
        x: 0,
        y: 0,
        width: 595,
        height: 842,
      };

      // Pour les supports sociaux, ne convertir les produits en éléments que si aucun élément n'existe déjà
      if (
        supportType.value === "social" &&
        Array.isArray(support.products) &&
        (!page.elements || page.elements.length === 0)
      ) {
        const elements: any[] = [];
        support.products.forEach((product: any, index: number) => {
          if (product) {
            const customization = productCustomizationStore.getElementSettings(
              product.id
            );
            const element = {
              id: 0, // Sera généré par le backend
              type: "product",
              support_page_id: page.id || 0,
              name: product.name || "",
              price_ht: product.price?.toString() || "0.00",
              price_ttc: product.price_ttc?.toString() || "0.00",
              price_old: product.price_old?.toString() || "0.00",
              discount_mode: "0", // Convertir en string
              description: product.description || "",
              hook: product.hook || product.name || "", // Utiliser le nom comme fallback
              pictogram_id: product.pictogram_id || null, // Utiliser null au lieu de 0
              product_image_id: product.product_image_id || null, // Toujours null pour éviter les erreurs de validation
              brand_id: product.brand_id || null, // Toujours null pour éviter les erreurs de validation
              product_id: product.id,
              element_settings: {
                ...customization,
                locked: false,
                opacity: 1,
                visible: true,
                blend_mode: "normal",
              },
              position_x: 0,
              position_y: 0,
              width: 100,
              height: 100,
              rotation: 0,
              z_index: index,
              custom_styles: [], // Utiliser un tableau vide au lieu de null
            };
            elements.push(element);
          }
        });
        preparedPage.elements = elements;
      } else if (Array.isArray(page.elements)) {
        preparedPage.elements = page.elements.map((element: any) => {
          const cleanElement = { ...element };

          // Nettoyer les champs obligatoires pour éviter les erreurs de validation
          cleanElement.pictogram_id =
            cleanElement.pictogram_id && cleanElement.pictogram_id > 0
              ? cleanElement.pictogram_id
              : null;
          cleanElement.product_image_id =
            cleanElement.product_image_id && cleanElement.product_image_id > 0
              ? cleanElement.product_image_id
              : null;
          cleanElement.brand_id =
            cleanElement.brand_id && cleanElement.brand_id > 0
              ? cleanElement.brand_id
              : null;

          // Nettoyer le champ product_image pour s'assurer qu'il soit une chaîne de caractères
          if (cleanElement.product_image) {
            if (
              typeof cleanElement.product_image === "object" &&
              cleanElement.product_image.image_name
            ) {
              cleanElement.product_image =
                cleanElement.product_image.image_name;
            } else if (typeof cleanElement.product_image !== "string") {
              cleanElement.product_image = "";
            }
          }

          // Si c'est un élément produit, ajouter les paramètres de personnalisation
          if (element.type === "product" && element.product_id) {
            const customization = productCustomizationStore.getElementSettings(
              element.product_id
            );
            cleanElement.element_settings = {
              ...customization,
              // Ajouter d'autres paramètres spécifiques à l'élément si nécessaire
              element_type: "product",
              element_id: element.id,
              product_id: element.product_id,
            };
          }

          delete cleanElement.image;
          delete cleanElement.product_image_real_id;
          cleanElement.hook = cleanElement.hook || cleanElement.name || "";
          return cleanElement;
        });
      } else {
        preparedPage.elements = [];
      }

      return preparedPage;
    });

    return preparedSupport;
  }

  // Fonction pour jouer un son
  const playSound = (sound: HTMLAudioElement) => {
    sound.currentTime = 0; // Réinitialiser le son
    sound.play().catch((error) => {
      console.warn("Erreur lors de la lecture du son:", error);
    });

    // Couper le son après 200ms pour le son iPhone
    setTimeout(() => {
      sound.pause();
      sound.currentTime = 0;
    }, 400);
  };

  const saveSupport = async (
    supportData?: Partial<Support>,
    options?: { skipAutoHqPdf?: boolean }
  ) => {
    try {
      isLoading.value = true;
      error.value = null;

      if (!currentSupport.value && !supportData) {
        throw new Error("Aucun support à sauvegarder");
      }

      const dataToSave = supportData || currentSupport.value;
      if (!dataToSave) {
        throw new Error("Aucune donnée à sauvegarder");
      }

      // Vérification des champs obligatoires
      if (!dataToSave.name) {
        throw new Error("Le nom du support est obligatoire");
      }
      if (!dataToSave.dates) {
        throw new Error("La période de validité est obligatoire");
      }
      if (
        !dataToSave.pages ||
        !Array.isArray(dataToSave.pages) ||
        dataToSave.pages.length === 0
      ) {
        throw new Error("Le support doit contenir au moins une page");
      }

      const preparedSupport = prepareSupportForSave(dataToSave as Support);

      // Ne redéfinir le support_template_id que pour les nouveaux supports
      if (!preparedSupport.id) {
        if (supportType.value === "flyer") {
          preparedSupport.support_template_id = 2;
        } else if (supportType.value === "depliant") {
          preparedSupport.support_template_id = 3;
        } else if (supportType.value === "banderole") {
          preparedSupport.support_template_id = 5;
        } else if (supportType.value === "emailing") {
          preparedSupport.support_template_id = 4;
        } else if (supportType.value === "social") {
          preparedSupport.support_template_id = 6;
        }
      }

      if (supportType.value === "social") {
        preparedSupport.network = currentSupport.value?.network || "facebook";
        // Ne pas forcer le format pour les supports sociaux, laisser le format sélectionné
      }

      const cleanSupport = {
        ...preparedSupport,
        custom_styles: preparedSupport.custom_styles || [],
        display_offre: preparedSupport.display_offre || "none",
        products: preparedSupport.products || [],
        html: preparedSupport.html || "",
      };

      const payload = JSON.parse(JSON.stringify(cleanSupport));

      // Log de débogage pour identifier les IDs problématiques (désactivé)
      // console.log("=== DEBUG SUPPORT PAYLOAD ===");
      // console.log("Type de support:", payload.type);
      // if (payload.pages && payload.pages.length > 0) {
      //   payload.pages.forEach((page: any, pageIndex: number) => {
      //     console.log(`Page ${pageIndex}:`, {
      //       elements_count: page.elements?.length || 0,
      //       elements: page.elements?.map((el: any, elIndex: number) => ({
      //         index: elIndex,
      //         type: el?.type,
      //         pictogram_id: el?.pictogram_id,
      //         brand_id: el?.brand_id,
      //         product_image_id: el?.product_image_id,
      //         name: el?.name
      //       }))
      //     });
      //   });
      // }
      // console.log("===============================");

      // Nettoyage final et agressif des IDs invalides avant envoi
      if (payload.pages) {
        payload.pages.forEach((page: any) => {
          if (page.elements) {
            page.elements.forEach((element: any) => {
              if (element) {
                // Suppression complète des champs null, undefined, 0, ou vides
                if (
                  !element.pictogram_id ||
                  element.pictogram_id === 0 ||
                  element.pictogram_id === "0"
                ) {
                  delete element.pictogram_id;
                }
                if (
                  !element.brand_id ||
                  element.brand_id === 0 ||
                  element.brand_id === "0"
                ) {
                  delete element.brand_id;
                }
                if (
                  !element.product_image_id ||
                  element.product_image_id === 0 ||
                  element.product_image_id === "0"
                ) {
                  delete element.product_image_id;
                }
              }
            });
          }
        });
      }

      // console.log("=== AFTER FINAL CLEANUP ===");
      // if (payload.pages && payload.pages.length > 0) {
      //   payload.pages.forEach((page: any, pageIndex: number) => {
      //     console.log(`Page ${pageIndex} (after cleanup):`, {
      //       elements: page.elements?.map((el: any, elIndex: number) => ({
      //         index: elIndex,
      //         type: el?.type,
      //         pictogram_id: el?.pictogram_id,
      //         brand_id: el?.brand_id,
      //         product_image_id: el?.product_image_id,
      //         name: el?.name
      //       }))
      //     });
      //   });
      // }
      // console.log("==============================");

      let response;
      try {
        if (payload?.id) {
          response = await api.put(`/supports/${payload.id}`, payload);
        } else {
          response = await api.post("/supports", payload);
        }

        // sendPagesToBackend(payload.pages);

        // Vérifier si la réponse est valide
        if (!response?.data) {
          throw new Error("Réponse invalide de l'API");
        }

        // Extraire les données du support de la réponse
        const supportData = response.data.data;
        if (!supportData) {
          throw new Error("Données du support non trouvées dans la réponse");
        }

        // Vérifier si nous avons un ID dans la réponse
        const supportId = supportData.id;
        if (!supportId) {
          throw new Error("L'ID du support n'a pas été retourné par l'API");
        }

        // Enrichir les images produits
        const supportWithImages = await enrichirImagesProduits(supportData);
        // Préserver le repiquage (champ éditeur-only, absent de la réponse
        // serveur) : sinon il est perdu avant que persistSupportPdf ne l'envoie.
        const previousRepiquage = (currentSupport.value as any)?.repiquage;
        currentSupport.value = supportWithImages;
        if (previousRepiquage && currentSupport.value) {
          (currentSupport.value as any).repiquage = previousRepiquage;
        }

        // Lancer automatiquement la génération PDF haute qualité après sauvegarde (si activé et non désactivé explicitement)
        if (autoGenerateHighQualityPdf.value && !options?.skipAutoHqPdf) {
          if (supportId) {
            triggerAsyncHighQualityPdfGeneration(supportId);
          }
        }

        // Retourner un objet avec le message de succès et les données
        return {
          message: response.data.message || "Support sauvegardé avec succès",
          data: supportWithImages,
          preview_url: response.data.preview_url,
        };
      } catch (apiError: any) {
        if (apiError.response?.status === 422) {
          const validationErrors = apiError.response.data.errors;
          const errorMessage = Object.values(validationErrors)
            .flat()
            .join(", ");
          playSound(errorSound);
          toast.error(`Erreur de validation : ${errorMessage}`);
          throw new Error(`Erreur de validation : ${errorMessage}`);
        }
        playSound(errorSound);
        toast.error(
          (apiError.response?.data as any)?.message || apiError.message
        );
        throw apiError;
      }
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Une erreur est survenue";
      playSound(errorSound);
      toast.error(error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Méthode pour lancer la génération PDF HQ de manière complètement asynchrone
  const triggerAsyncHighQualityPdfGeneration = (supportId: number) => {
    // Utiliser setTimeout pour s'assurer que c'est complètement asynchrone
    setTimeout(() => {
      requestHighQualityPdfGeneration(supportId)
        .then(() => {
          console.log(
            `Génération PDF haute qualité lancée automatiquement pour le support ${supportId}`
          );
        })
        .catch((pdfError) => {
          console.error(
            "Erreur lors du lancement automatique de la génération PDF HQ:",
            pdfError
          );
        });
    }, 100); // Petit délai pour s'assurer que le processus principal est terminé
  };

  const setSupportType = (type: SupportType) => {
    supportType.value = type;
  };

  // Méthode pour activer/désactiver la génération PDF automatique
  const setAutoGenerateHighQualityPdf = (enabled: boolean) => {
    autoGenerateHighQualityPdf.value = enabled;
  };

  const resetStore = () => {
    currentSupport.value = null;
    isLoading.value = false;
    error.value = null;
    isEditing.value = false;
    isPreview.value = false;
    supportType.value = "affiche";
  };

  const togglePreview = () => {
    isPreview.value = !isPreview.value;
  };

  // Envoie les images de toutes les pages au backend pour générer le PDF et l'image de preview
  const sendPagesToBackend = async (base64CanvasArray: string[]) => {
    try {
      if (!currentSupport.value) throw new Error("Aucun support courant");
      const supportTypeValue = currentSupport.value.type || supportType.value;
      const brandSlug = localStorage.getItem("brand") || "hv";
      const supportId = currentSupport.value.id;
      // Pour le PDF, on envoie toutes les pages
      const payload = {
        base64CanvasArray,
        supportType: supportTypeValue,
        brandSlug,
        supportId,
      };
      const token = localStorage.getItem("auth_token");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      await api.post("/supports/preview/pdf", payload, {
        headers,
        withCredentials: false,
      });
    } catch (error) {
      console.error("Erreur lors de l'envoi des pages au backend:", error);
      throw error;
    }
  };

  // Nouvelle méthode : demande la génération PDF haute qualité via job backend
  const requestHighQualityPdfGeneration = async (supportId: number) => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await api.post(
        `/supports/${supportId}/generate-hq-pdf`,
        {},
        {
          headers,
          withCredentials: false,
          timeout: 60000, // 60 secondes - Le backend peut prendre du temps pour initialiser le job
        }
      );

      if (response.status >= 400) {
        throw new Error(
          response.data?.message ||
            "Erreur lors du lancement de la génération PDF"
        );
      }

      return response.data; // Contient job_id et status
    } catch (error: unknown) {
      console.error("Erreur lors de la demande de génération PDF HQ:", error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        throw new Error(
          (axiosError.response?.data as any)?.message || axiosError.message
        );
      }
      throw error;
    }
  };

  // Méthode pour vérifier l'état d'un job PDF
  const checkPdfJobStatus = async (jobId: string) => {
    try {
      // Vérifier que le jobId est valide
      if (!jobId || jobId === "undefined" || jobId.trim() === "") {
        throw new Error(`ID de job invalide: ${jobId}`);
      }

      const token = localStorage.getItem("auth_token");
      const headers: Record<string, string> = {
        Accept: "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await api.get(`/jobs/pdf/${jobId}/status`, {
        headers,
        withCredentials: false,
      });

      return response.data; // { status: 'pending'|'processing'|'completed'|'failed', progress: 0-100, download_url?: string, error?: string }
    } catch (error: unknown) {
      console.error("Erreur lors de la vérification du job PDF:", error);
      throw error;
    }
  };

  // Envoie le PDF généré (en blob) au backend pour stockage et génération de la preview
  const sendPdfBlobToBackend = async (formData: FormData) => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers: Record<string, string> = {
        // Ne pas définir Content-Type ici, il sera automatiquement défini avec la boundary correcte
        Accept: "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await api.post("/supports/preview/pdf", formData, {
        headers,
        withCredentials: false,
        // Augmenter le timeout pour les gros fichiers
        timeout: 30000,
        // Ajouter la gestion des erreurs CORS
        validateStatus: (status) => {
          return status >= 200 && status < 500;
        },
      });

      if (response.status === 413) {
        throw new Error("Le fichier PDF est trop volumineux");
      }

      if (response.status === 415) {
        throw new Error("Le format du fichier n'est pas supporté");
      }

      if (response.status >= 400) {
        throw new Error(
          response.data?.message || "Erreur lors de l'envoi du PDF"
        );
      }

      return response.data;
    } catch (error: unknown) {
      console.error("Erreur détaillée lors de l'envoi du PDF:", error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.code === "ECONNABORTED") {
          throw new Error("Le délai d'attente pour l'envoi du PDF a expiré");
        }
        if (axiosError.response?.status === 0) {
          throw new Error("Erreur CORS: Impossible d'accéder au serveur");
        }
        throw new Error(
          (axiosError.response?.data as any)?.message || axiosError.message
        );
      }
      throw error;
    }
  };

  /**
   * Persiste le PDF d'un support : tente la génération VECTORIELLE ASYNCHRONE
   * côté serveur (job + notification via polling, géré par usePdfJobsStore), et
   * ne génère la capture client (lente) qu'en dernier recours, si le vectoriel
   * est indisponible/désactivé (422) ou en erreur. Sauvegarde fluide.
   */
  const persistSupportPdf = async (
    formData: FormData,
    makeBlob: () => Promise<Blob>,
    // Fournit le HTML EXACT de l'éditeur (generateFullHTML). S'il est donné, le
    // serveur le rend tel quel via Browsershot (fidélité pixel) au lieu de
    // reconstruire le rendu en Blade.
    getHtml?: () => Promise<string>,
    // Force la CAPTURE client (screenshot) et saute tout le pipeline vectoriel.
    // Utilisé pour banderole / social / emailing, où la capture donne un
    // meilleur résultat que le vectoriel.
    forceCapture = false
  ) => {
    const supportId = formData.get("supportId");
    const brandSlug = formData.get("brandSlug");
    const supportType = formData.get("supportType");
    const supportName =
      (currentSupport.value?.name as string) || "votre support";
    // Repiquage (signature) choisi dans l'éditeur — persisté au footer côté serveur.
    const repiquageId =
      (currentSupport.value as any)?.repiquage?.id ?? null;
    const repiquageZoom =
      (currentSupport.value as any)?.repiquage?.zoom ?? 1;
    // Sélection recto/verso/both (flyer) ; null pour les autres types.
    const selectedPages = formData.get("selectedPages");

    if (supportId && !forceCapture) {
      try {
        // HTML exact de l'éditeur (best-effort : on n'échoue pas la sauvegarde
        // si la génération HTML plante, le serveur basculera sur le Blade).
        let html: string | null = null;
        if (getHtml) {
          try {
            html = await getHtml();
          } catch (htmlError) {
            console.warn("generateFullHTML a échoué :", htmlError);
          }
        }

        const token = localStorage.getItem("auth_token");
        const headers: Record<string, string> = {
          Accept: "application/json",
        };
        if (token) headers["Authorization"] = `Bearer ${token}`;

        // IMPORTANT : le HTML est envoyé en FICHIER (multipart), jamais en
        // paramètre. Le pare-feu applicatif (FirewallPatch) scanne les valeurs
        // string de $request->all() pour des patterns d'attaque (<script,
        // on…=, etc.) et BLOQUE l'IP 24 h si match — un HTML complet en
        // paramètre déclenche ça à coup sûr. Les fichiers uploadés ne sont pas
        // scannés. Sans HTML, on garde le JSON simple.
        let response;
        if (html) {
          const fd = new FormData();
          fd.append("brandSlug", String(brandSlug ?? ""));
          fd.append("supportType", String(supportType ?? ""));
          if (repiquageId != null) fd.append("repiquageId", String(repiquageId));
          fd.append("repiquageZoom", String(repiquageZoom));
          if (selectedPages != null)
            fd.append("selectedPages", String(selectedPages));
          fd.append("html", new File([html], "support.html", { type: "text/html" }));

          response = await api.post(
            `/supports/${supportId}/generate-vector-pdf`,
            fd,
            {
              headers,
              withCredentials: false,
              timeout: 60000,
              validateStatus: (status) => status >= 200 && status < 500,
            }
          );
        } else {
          response = await api.post(
            `/supports/${supportId}/generate-vector-pdf`,
            { brandSlug, supportType, repiquageId, repiquageZoom, selectedPages },
            {
              headers: { ...headers, "Content-Type": "application/json" },
              withCredentials: false,
              timeout: 30000,
              validateStatus: (status) => status >= 200 && status < 500,
            }
          );
        }
        const jobId = response.data?.job_id;
        if (response.status >= 200 && response.status < 300 && jobId) {
          // Suivi global : notification de fin, survit à la navigation.
          usePdfJobsStore().track(jobId, supportName);
          return { async: true, jobId };
        }
        // 422 => rendu vectoriel indisponible : on bascule sur la capture.
      } catch (vectorError) {
        console.warn(
          "Rendu vectoriel async indisponible, bascule sur la capture :",
          vectorError
        );
      }
    }

    // Fallback : capture client (générée à la demande, seulement maintenant).
    const blob = await makeBlob();
    formData.append(
      "pdf",
      new File([blob], "support.pdf", { type: "application/pdf" })
    );
    const data = await sendPdfBlobToBackend(formData);
    return { async: false, data };
  };

  // Envoie le HTML généré au backend pour stockage
  const sendHtmlToBackend = async (formData: FormData) => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers: Record<string, string> = {
        Accept: "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await api.post("/supports/preview/html", formData, {
        headers,
        withCredentials: false,
        timeout: 30000,
        validateStatus: (status) => {
          return status >= 200 && status < 500;
        },
      });

      if (response.status === 413) {
        throw new Error("Le fichier HTML est trop volumineux");
      }

      if (response.status === 415) {
        throw new Error("Le format du fichier n'est pas supporté");
      }

      if (response.status >= 400) {
        throw new Error(
          response.data?.message || "Erreur lors de l'envoi du HTML"
        );
      }

      return response.data;
    } catch (error: unknown) {
      console.error("Erreur détaillée lors de l'envoi du HTML:", error);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.code === "ECONNABORTED") {
          throw new Error("Le délai d'attente pour l'envoi du HTML a expiré");
        }
        if (axiosError.response?.status === 0) {
          throw new Error("Erreur CORS: Impossible d'accéder au serveur");
        }
        throw new Error(
          (axiosError.response?.data as any)?.message || axiosError.message
        );
      }
      throw error;
    }
  };

  return {
    // État
    currentSupport,
    isLoading,
    error,
    isEditing,
    isPreview,
    supportType,
    autoGenerateHighQualityPdf,

    // Getters
    supportId,
    isNewSupport,

    // Actions
    loadSupport,
    initSupport,
    saveSupport,
    setSupportType,
    setAutoGenerateHighQualityPdf,
    resetStore,
    updateSupport,
    togglePreview,
    sendPagesToBackend,
    sendPdfBlobToBackend,
    persistSupportPdf,
    sendHtmlToBackend,

    // Nouvelles méthodes pour PDF haute qualité
    requestHighQualityPdfGeneration,
    triggerAsyncHighQualityPdfGeneration,
    checkPdfJobStatus,

    // Service de gestion des en-têtes de support
    supportHeadersService,
  };
});
